import { useRef, useState } from 'react'
import { BrandHomeLink } from './BrandHomeLink'
import { NavItem } from './NavItem'
import { OokeyLogo } from './OokeyLogo'
import { SidenavFooterMenu, type SidenavFooterMenuTarget } from './SidenavFooterMenu'
import { logout as defaultLogoutApi } from '@features/auth/api/authApi'
import { removePin as wipeLocalPinVault } from '@features/auth/services/pinVaultService'
import { sessionStore } from '@features/auth/services/sessionStore'
import { useUserPreferences } from '@features/preferences/hooks/useUserPreferences'
import { LOGIN_ROUTE } from '@constants/routes'
import { useOutsideClick } from '@hooks/useOutsideClick'
import { useTranslation } from '@/i18n'

/**
 * 左サイドバー。design/js/components.jsx:46-105 を移植。
 *
 * 違い (design → apps/web):
 *   - window.OK.user / items / sends 依存を props 化 (user / vaultCount / sendCount)
 *   - foot の popover メニューは SidenavFooterMenu に切り出し (T-B2 リファクタ)
 *   - outside-click handler は useOutsideClick フックに共通化
 *   - インライン style は components.css に移動
 */

export interface SidenavUser {
  initial: string
  name: string
  email: string
}

export interface SidenavProps {
  route: string
  go: (target: string) => void
  user?: SidenavUser
  vaultCount?: number
  sendCount?: number
  /** spec で差し替え可能な logout 実行関数。既定は POST /api/auth/logout (HttpOnly refresh
   *  cookie を破棄) → sessionStore.clear() (in-memory SK + material を zeroize) → #login。
   *  サーバが 401 等で失敗してもクライアント側のクリアは必ず実行する。
   */
  logout?: () => Promise<void>
}

const isHere = (route: string, head: string): boolean =>
  route === head || route.startsWith(`${head}/`)

export const NAV_GROUPS = [
  {
    labelKey: 'common.nav.section_main',
    items: [
      { head: 'home', icon: 'home' as const, labelKey: 'common.nav.home', target: 'home' },
      { head: 'vault', icon: 'vault' as const, labelKey: 'common.nav.vault', target: 'vault' },
      {
        head: 'tools',
        icon: 'key' as const,
        labelKey: 'common.nav.tools',
        target: 'tools/generator',
      },
      { head: 'send', icon: 'send' as const, labelKey: 'common.nav.send', target: 'send' },
      {
        head: 'reports',
        icon: 'shield' as const,
        labelKey: 'common.nav.reports',
        target: 'reports',
      },
    ],
  },
  {
    labelKey: 'common.nav.section_settings',
    items: [
      {
        head: 'settings',
        icon: 'settings' as const,
        labelKey: 'common.nav.settings',
        target: 'settings/account',
      },
    ],
  },
] as const

const performLogout = async (logoutApi: () => Promise<void>): Promise<void> => {
  // サーバ側 refresh cookie の破棄を試みる (失敗しても続行: クライアント側の
  // sessionStore.clear() は **必ず** 走らせる)。失敗ログは握りつぶし。
  try {
    await logoutApi()
  } catch {
    // 401 / network 等: 無視。次の clear() で session を確実に閉じる。
  }
  sessionStore.clear()
  // PIN vault は端末ローカルの wrap-SK。multi-account 切替時に前ユーザーの
  // SK が残らないよう logout で必ず破棄する (TASK-WEB-PIN review I-1)。
  // IDB 失敗時もユーザー操作は logout として完了させる (await したまま握り潰し)。
  try {
    await wipeLocalPinVault()
  } catch {
    // IDB 不能環境 (quota / private mode) — 次回 PIN unlock で userEmail
    // ミスマッチ判定で auto-clear されるため最悪でも 1 試行で vault は消える。
  }
}

export const Sidenav = ({
  route,
  go,
  user,
  vaultCount,
  sendCount,
  logout = defaultLogoutApi,
}: SidenavProps): JSX.Element => {
  const { t } = useTranslation()
  const { preferences } = useUserPreferences()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const footRef = useRef<HTMLDivElement | null>(null)

  useOutsideClick(footRef, isMenuOpen, () => setIsMenuOpen(false))

  const onMenuSelect = (target: SidenavFooterMenuTarget): void => {
    setIsMenuOpen(false)
    if (target === 'logout') {
      // logout は async だが UI は即時に menu を閉じて #login へ navigate する。
      // サーバ呼出と sessionStore.clear() は背景で実行。
      void performLogout(logout).finally(() => {
        go(LOGIN_ROUTE)
      })

      return
    }
    if (target === 'lock') {
      // 「今すぐロック」: UNLOCKED → LOCKED 遷移を明示的に行ってから #lock へ。
      // ここで sessionStore.lock() を呼ばないと SK がメモリに残り続け、PIN /
      // master password 再入力なしで vault に再アクセスできる状態になる
      // (security bug)。LockPage 側は LOCKED 前提で PIN status を初期化する
      // ため、これを呼ばずに navigate すると PIN keypad が出ない場合がある。
      sessionStore.lock()
    }
    go(target)
  }

  const badgeFor = (head: string): number | undefined => {
    if (head === 'vault') return vaultCount
    if (head === 'send') return sendCount

    return undefined
  }

  return (
    <aside className="sidenav" data-testid="sidenav">
      <BrandHomeLink className="brand" ariaLabel={t('common.app.name')}>
        <span className="logo">
          <OokeyLogo width={44} />
        </span>
        <span className="nm">{t('common.app.name')}</span>
      </BrandHomeLink>

      <div className="body">
        {NAV_GROUPS.map(group => (
          <div className="group" key={group.labelKey}>
            <div className="group-label">{t(group.labelKey)}</div>
            {group.items.map(item => (
              <NavItem
                key={item.head}
                icon={item.icon}
                label={t(item.labelKey)}
                active={isHere(route, item.head)}
                badge={badgeFor(item.head)}
                onClick={() => go(item.target)}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="foot" ref={footRef}>
        <div className="avatar" data-avatar-color={preferences.avatarColor}>
          {user?.initial ?? ''}
        </div>
        <div className="meta">
          <div className="nm">{user?.name ?? ''}</div>
          <div className="em">{user?.email ?? ''}</div>
        </div>
        <button
          type="button"
          className="icon-btn"
          title={t('common.ui.menu')}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-expanded={isMenuOpen}
          aria-haspopup="menu"
        >
          <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx={12} cy={5} r={1} />
            <circle cx={12} cy={12} r={1} />
            <circle cx={12} cy={19} r={1} />
          </svg>
        </button>
        {isMenuOpen && <SidenavFooterMenu onSelect={onMenuSelect} />}
      </div>
    </aside>
  )
}
